import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useUser } from "../../../../context/UserContext";
import ConfirmationModal from "../../../test_box/components/ConfirmationModal";
import authService from "../../../../services/authService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CustomFunction = forwardRef(({ instructions }, ref) => {
  const { token } = useUser();
  const [customPrompts, setCustomPrompts] = useState({
    tools: [],
    dynamic_prompt:
      "You are a helpful assistant that can use the function when appropriate.",
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [functionForm, setFunctionForm] = useState({
    name: "",
    description: "",
    url: "",
    request_type: "GET",
    parameters: "{}",
  });

   // Expose a method to update the dynamic prompt
   useImperativeHandle(ref, () => ({
    updateDynamicPrompt: async () => {
      try {
        const updatedPrompt = {
          ...customPrompts,
          dynamic_prompt: instructions, // Use instructions from props
        };

        await authService.updateCustomPrompt(token, updatedPrompt);

        toast.success("Dynamic prompt updated successfully", {
          position: "top-right",
          autoClose: 3000,
        });

        return true;
      } catch (err) {
        console.error(err);
        toast.error(
          err.response?.data?.message || "Failed to update dynamic prompt",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        return false;
      }
    },
  }));

  useEffect(() => {
    const fetchCustomPrompts = async () => {
      if (!token) {
        console.error("No authentication token available");
        return;
      }

      try {
        const data = await authService.fetchCustomPrompts(token);

        if (data && typeof data === "object") {
          const { _id, user, __v, ...rest } = data;
          setCustomPrompts(rest);

          if (!rest.tools || !Array.isArray(rest.tools)) {
            setCustomPrompts((prev) => ({
              ...prev,
              tools: [],
            }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch custom prompts:", err);
        toast.error(
          err.response?.data?.message || "Failed to fetch custom prompts",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCustomPrompts();
  }, [token]);

  const handleEdit = (functionTool, index) => {
    if (functionTool) {
      setIsEditing(true);
      setSelectedFunction({ index });

      setFunctionForm({
        name: functionTool.name || "",
        description: functionTool.description || "",
        url: functionTool.webhook_url || "",
        request_type: functionTool.request_type || "GET",
        parameters: JSON.stringify(functionTool.parameters || {}, null, 2),
      });

      setShowModal(true);
    }
  };

  const handleAdd = () => {
    setIsEditing(false);
    setSelectedFunction(null);
    setFunctionForm({
      name: "",
      description: "",
      url: "",
      request_type: "POST",
      parameters: JSON.stringify(
        {
          type: "object",
          properties: {
            example_param: {
              type: "string",
              description: "Example parameter description",
            },
          },
          required: ["example_param"],
        },
        null,
        2
      ),
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFunctionForm({
      ...functionForm,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const functionData = {
        type: "function",
        name: functionForm.name.includes(" ")
          ? functionForm.name.split(" ").join("_").toLowerCase()
          : functionForm.name.toLowerCase(),
        description: functionForm.description,
        webhook_url: functionForm.url,
        request_type: functionForm.request_type,
        parameters:
          functionForm.parameters.trim() === ""
            ? {}
            : JSON.parse(functionForm.parameters),
      };

      let updatedTools = [...(customPrompts.tools || [])];

      if (isEditing && selectedFunction) {
        updatedTools[selectedFunction.index] = functionData;
      } else {
        updatedTools.push(functionData);
      }

      const promptData = {
        tools: updatedTools,
        dynamic_prompt: customPrompts.dynamic_prompt,
      };

      await authService.updateCustomPrompt(token, promptData);

      toast.success(
        isEditing
          ? "Function updated successfully"
          : "Function created successfully",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      setCustomPrompts({
        ...customPrompts,
        tools: updatedTools,
      });

      handleCancel();
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message ||
          (isEditing
            ? "Failed to update function"
            : "Failed to create function"),
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setIsEditing(false);
    setSelectedFunction(null);
    setFunctionForm({
      name: "",
      description: "",
      url: "",
      parameters: "{}",
    });
  };

  const handleDelete = (index) => {
    setDeleteIndex(index);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteIndex !== null) {
      try {
        const updatedTools = [...customPrompts.tools];
        updatedTools.splice(deleteIndex, 1);

        const updatedPrompt = {
          ...customPrompts,
          tools: updatedTools,
        };

        await authService.updateCustomPrompt(token, updatedPrompt);

        toast.success("Function deleted successfully", {
          position: "top-right",
          autoClose: 3000,
        });

        setCustomPrompts({
          ...customPrompts,
          tools: updatedTools,
        });
      } catch (err) {
        console.error(err);
        toast.error(
          err.response?.data?.message || "Failed to delete function",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } finally {
        setIsDeleteModalOpen(false);
        setDeleteIndex(null);
      }
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  const FunctionsSkeleton = () => (
    <div>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="list-group-item d-flex justify-content-between align-items-center"
        >
          <div className="d-flex align-items-center">
            <div
              className="placeholder-glow me-3"
              style={{ width: "24px", height: "24px" }}
            >
              <span className="placeholder col-12 h-100"></span>
            </div>
            <span className="placeholder-glow">
              <span className="placeholder col-8"></span>
            </span>
          </div>
          <div>
            <button className="btn btn-sm btn-outline-secondary me-2 disabled">
              <span
                className="placeholder col-12"
                style={{ width: "20px" }}
              ></span>
            </button>
            <button className="btn btn-sm btn-outline-secondary disabled">
              <span
                className="placeholder col-12"
                style={{ width: "20px" }}
              ></span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const getFunctionDisplayName = (func) => {
    return func.name || func.id || "Unnamed Function";
  };

  return (
    <div className="d-flex custom-function-container p-4 position-absolute w-100">
      <div className="dashboard-content flex-grow-1 pt-1 p-4 ps-5">
        <div className="mb-4">
          <div className="text-center mb-4">
            <Button
              variant="none"
              className="px-5 py-2 fw-bold btn-group purple-color"
              onClick={handleAdd}
            >
              <img src="/assets/plus-blue.svg" alt="Info" className="me-2" />{" "}
              Add
            </Button>
          </div>

          {loading ? (
            <FunctionsSkeleton />
          ) : (
            <div className="list-group">
              {!customPrompts.tools || customPrompts.tools.length === 0 ? (
                <div className="alert alert-info">
                  No functions found. Click the Add button to create a new
                  function.
                </div>
              ) : (
                customPrompts.tools.map((tool, index) => {
                  if (!tool || tool.type !== "function") {
                    return null;
                  }

                  return (
                    <div
                      className="my-1 py-3 border rounded-3 fw-bold list-group-item d-flex justify-content-between align-items-center"
                      key={index}
                    >
                      <div className="d-flex align-items-center">
                        <span>{getFunctionDisplayName(tool)}</span>
                      </div>
                      <div>
                        <button
                          className="btn btn-sm  me-2"
                          onClick={() => handleEdit(tool, index)}
                        >
                          <img
                            src="/assets/edit-1.svg"
                            alt="Edit"
                            className="icon-sm"
                            title="Edit"
                          />
                        </button>
                        <button
                          className="btn btn-sm me-2"
                          onClick={() => handleDelete(index)}
                        >
                          <img
                            src="/assets/trash-2.svg"
                            alt="Delete"
                            className="icon-sm"
                            title="Delete"
                            style={{ width: "24px", height: "24px" }}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this function? This action cannot be undone."
      />

      {showModal && (
        <div
          className="modal"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fs-three">
                  {isEditing ? "Edit Function" : "Add Custom Function"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancel}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="my-3">
                    <label
                      htmlFor="name"
                      className="form-label fw-bold gray-color"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      placeholder="Enter the name of the custom function"
                      value={functionForm.name}
                      onChange={handleChange}
                      required
                    />
                    <small className="py-2">
                      The name should be unique and separated with underscore.
                    </small>
                  </div>

                  <div className="my-4">
                    <label
                      htmlFor="description"
                      className="form-label fw-bold gray-color"
                    >
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      value={functionForm.description}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Enter a description..."
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="url"
                      className="form-label fw-bold gray-color"
                    >
                      Your URL
                    </label>
                    <div className="input-group">
                      <select
                        className="form-select"
                        id="request_type"
                        name="request_type"
                        value={functionForm.request_type}
                        onChange={handleChange}
                        style={{ maxWidth: "100px" }}
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                      </select>
                      <input
                        type="url"
                        className="form-control"
                        id="url"
                        name="url"
                        value={functionForm.url}
                        onChange={handleChange}
                        required
                        placeholder="Enter the URL of the custom function"
                      />
                    </div>
                  </div>

                  <div className="my-4">
                    <label
                      htmlFor="parameters"
                      className="form-label fw-bold gray-color mb-0"
                    >
                      Parameters (Optional)
                    </label>
                    <div className="form-text mb-2">
                      JSON schema that defines the format in which the LLM will
                      return. Please refer to the docs.
                    </div>
                    <textarea
                      className="form-control bg-dark text-light mb-5"
                      id="parameters"
                      name="parameters"
                      value={functionForm.parameters}
                      onChange={handleChange}
                      rows="10"
                      style={{ fontFamily: "monospace" }}
                    ></textarea>
                  </div>

                  <div className="modal-footer d-flex justify-content-between align-items-center">
                    <div className="support-btn-container d-flex align-items-center">
                      <Link
                        to=""
                        className="fs-one text-decoration-none purple-color fw-bold me-2"
                      >
                        Need Help?
                      </Link>

                      <button
                        type="button"
                        className="btn fw-bold btn-outline-secondary d-flex support-button"
                      >
                        <img
                          src="/assets/life-buoy.svg"
                          alt="Info"
                          className="me-1 icon-svg"
                        />{" "}
                        Contact Support
                      </button>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="me-3 fs-one gray-color">
                        Changes will go live immediately
                      </span>
                      <button
                        type="button"
                        className="btn fw-bold"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary fw-bold">
                        {isEditing ? "Update" : "Save and add"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default CustomFunction;
